import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import VehicleAndUniforms, {
  type VehicleAndUniformsHandle,
} from "@/components/features/AlignmentSteps/VehicleAndUniforms/VehicleAndUniforms";
import JsonFormBuilder from "@/components/features/JsonFormBuilder/JsonFormBuilder";
import Rates from "@/components/features/RateManagement/Rates";
import { ServiceProviderHeader } from "@/components/features/ServiceProviderHeader/ServiceProviderHeader";
import { DoNotUsePanel } from "@/components/features/ServiceProviders/DoNotUse/DoNotUsePanel";
import { DoNotUseReasonModal } from "@/components/features/ServiceProviders/DoNotUse/DoNotUseReasonModal";
import { ServiceProviderChangePasswordModal } from "@/components/features/ServiceProviders/ChangePassword/ServiceProviderChangePasswordModal";
import { AlignmentSPEditTab } from "@/components/features/ServiceProviders/Alignment/AlignmentSPEditTab";
import Trades from "@/components/features/ServiceProviders/Trades/ServiceProviderTrades";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useServiceProviderDetails } from "@/hooks/service-provider/useServiceProviderDetails";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Flex, Tabs } from "@mantine/core";
import { ServiceProviderDetailSkeleton, TabContentFormSkeleton } from "@/components/ui/Skeleton";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { z } from "zod";
import styles from "./serviceProviderDetail.module.scss";
import LicensesAndCertificates from "@/components/features/ServiceProviders/LicensesAndCertificates/LicensesAndCertificates";
import W2Coverage from "@/components/features/ServiceProviders/W2Coverage/W2Coverage";
import Compensation from "@/components/features/ServiceProviders/Compensation/Compensation";
import { ScrollArea } from "@/components/ui/ScrollArea/ScrollArea.tsx";
import { SPDocumentsTab } from "@/components/domain/ServiceProvider/Documents";
import { SPW9Tab } from "@/components/domain/ServiceProvider/W9";
import { SpPaymentLinkPanel } from "@/components/domain/ServiceProvider/Payments";

export const Route = createFileRoute("/_authenticated/service-providers/$id")({
  component: ServiceProviderDetail,
  validateSearch: (search) => searchParamsSchema.parse(search),
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

export type TProviderType = "Vendor" | "Independent Tech" | "Owner Operator" | "Mom & Pop" | "W2";

type TabConfig = {
  value:
    | "owner-info"
    | "applicant-info"
    | "business-info"
    | "trades"
    | "compensation"
    | "rates"
    | "payments"
    | "coverage"
    | "vehicle"
    | "alignment"
    | "compliance"
    | "license-and-certificates"
    | "documents"
    | "w9";
  label: string;
  component?: (id: string, providerType?: string) => ReactNode;
  /**
   * Rendered above the tab's own form. Lets a tab add domain UI without giving
   * up the generic form renderer, which `component` replaces outright.
   */
  panel?: (id: string) => ReactNode;
};

const searchParamsSchema = z.object({
  tab: z
    .enum([
      "owner-info",
      "applicant-info",
      "business-info",
      "trades",
      "compensation",
      "rates",
      "compensation",
      "payments",
      "coverage",
      "vehicle",
      "compliance",
      "alignment",
      "license-and-certificates",
      "documents",
      "w9",
    ])
    .optional()
    .catch("owner-info"),
});

const TABS: TabConfig[] = [
  { value: "owner-info", label: "Owner Info" },
  { value: "business-info", label: "Business Info" },
  { value: "trades", label: "Trades & Equipment", component: () => <Trades isDetailPage={true} /> },
  {
    value: "rates",
    label: "Rates",
    component: (id, providerType) => (
      <Rates id={id} isDetailPage={true} providerType={providerType} />
    ),
  },
  { value: "vehicle", label: "Vehicles & Appearance" },
  {
    value: "payments",
    label: "Payments",
    // A panel rather than a component: entering payment details directly is
    // still a supported path, so the provider-supplied one sits above that form
    // instead of replacing it.
    panel: (id) => <SpPaymentLinkPanel serviceProviderId={id} />,
  },
  { value: "coverage", label: "Coverage" },
  {
    value: "license-and-certificates",
    label: "Licenses & Certificates",
    component: (id) => <LicensesAndCertificates spId={id} />,
  },
  { value: "w9", label: "W9", component: (id) => <SPW9Tab serviceProviderId={id} /> },
  {
    value: "documents",
    label: "Documents",
    component: (_id, providerType) => <SPDocumentsTab providerType={providerType} />,
  },
  {
    value: "alignment",
    label: "Alignment",
    component: (id) => <AlignmentSPEditTab serviceProviderId={id} />,
  },
];

const W2TABS: TabConfig[] = [
  { value: "applicant-info", label: "Applicant Info" },
  {
    value: "trades",
    label: "Trades & Equipment",
    component: () => <Trades isDetailPage={true} showW2Vehicle />,
  },
  {
    value: "compensation",
    label: "Compensation",
    component: (id) => <Compensation serviceProviderId={id} />,
  },
  {
    value: "coverage",
    label: "Coverage",
    component: (id) => <W2Coverage sourceId={id} viewMode={true} detailsPage={true} />,
  },
  { value: "vehicle", label: "Vehicles & Appearance" },
  {
    value: "license-and-certificates",
    label: "Licenses & Certificates",
    component: (id) => <LicensesAndCertificates spId={id} />,
  },
  { value: "w9", label: "W9", component: (id) => <SPW9Tab serviceProviderId={id} /> },
  {
    value: "documents",
    label: "Documents",
    component: (_id, providerType) => <SPDocumentsTab providerType={providerType} />,
  },
  {
    value: "alignment",
    label: "Alignment",
    component: (id) => <AlignmentSPEditTab serviceProviderId={id} />,
  },
];

const INDEPENDENT_TECH_TABS: TabConfig[] = [
  { value: "applicant-info", label: "Applicant Info" },
  ...TABS.slice(1),
];

const PROVIDER_TABS_MAP: Record<TProviderType, TabConfig[]> = {
  Vendor: TABS,
  W2: W2TABS,
  "Owner Operator": TABS,
  "Mom & Pop": TABS,
  "Independent Tech": INDEPENDENT_TECH_TABS,
};

function ServiceProviderDetail() {
  const { id } = useParams({ from: "/_authenticated/service-providers/$id" });
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/service-providers/$id" }) as {
    tab?: string;
  };
  const uiActiveTab = searchParams.tab ?? "owner-info";
  const vehicleAndUniformsRef = useRef<VehicleAndUniformsHandle>(null);

  const { hasAccess, isServiceProvider } = usePermissions();
  const { edit: canEditServiceProvider } = hasAccess({ module: MODULE_KEYS_MAP.serviceProviders });
  const [isDoNotUseModalOpen, setIsDoNotUseModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  const currentTabConfig = useMemo(
    () => TABS.find((tab) => tab.value === uiActiveTab),
    [uiActiveTab],
  );
  const hasComponent = !!currentTabConfig?.component;
  const tabForDataFetch = hasComponent ? "" : uiActiveTab;

  const {
    currentTabFormData,
    isLoading,
    isCurrentTabLoading,
    isSaving,
    methods,
    handleSaveClick,
    serviceProviderInfo,
  } = useServiceProviderDetails(id, tabForDataFetch);

  const providerName = serviceProviderInfo?.name;
  const hasValidName = providerName && providerName !== "N/A";
  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Service Providers",
        href: "/service-providers",
        icon: getRouteIcon(MODULE_KEYS_MAP.serviceProviders, 16),
      },
      { title: hasValidName ? providerName : "Loading...", href: `/service-providers/${id}` },
    ],
    [hasValidName, providerName, id],
  );
  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  // SERV-1936: Hide call and message options for SP
  // const contactOptions = [
  //   { id: "email", label: "Email", value: "email" },
  //   { id: "call", label: "Call", value: "call" },
  //   { id: "message", label: "Message", value: "message" },
  // ];
  const contactOptions = [{ id: "email", label: "Email", value: "email" }];

  const providerType = serviceProviderInfo?.type as TProviderType | undefined;

  useEffect(() => {
    if (!searchParams.tab && providerType) {
      const defaultTab =
        providerType === "W2" || providerType === "Independent Tech"
          ? "applicant-info"
          : "owner-info";
      navigate({
        to: "/service-providers/$id",
        params: { id },
        search: { tab: defaultTab as TabConfig["value"] },
        replace: true,
      });
    }
  }, [searchParams.tab, navigate, id, providerType]);
  const detailsTabs = useMemo(() => {
    if (!providerType) return [];
    return PROVIDER_TABS_MAP[providerType] ?? [];
  }, [providerType]);

  const handleContactClick = () => {};
  const handleBeginAlignmentClick = () => {
    const pt = serviceProviderInfo?.type;
    navigate({
      to: "/service-providers/$id/alignment/$providerType",
      params: {
        id,
        providerType: pt.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase(),
      },
    });
  };

  const handleSectionSave = async (sectionKey: string, valuesOverride?: Record<string, any>) => {
    await handleSaveClick(sectionKey, valuesOverride);
  };

  if (isLoading) return <ServiceProviderDetailSkeleton />;

  return (
    // I was not able to get into this screen
    // Temporary comment: minHeight "100vh" causes extra scroll — AppShell.Main already offsets the header, so 100vh overflows the scrollable area. Change to "100%" to fill parent without overflow.
    <Flex
      direction="column"
      style={{
        margin: "calc(var(--mantine-spacing-md) * -1)",
        backgroundColor: "var(--neutral-500)",
        minHeight: "100vh",
      }}
    >
      <ServiceProviderHeader
        name={serviceProviderInfo.name}
        company={serviceProviderInfo.company}
        type={serviceProviderInfo.type}
        status={serviceProviderInfo.status}
        currentStage={serviceProviderInfo.currentStage}
        daysInStage={serviceProviderInfo.daysInStage}
        daysSinceStarted={serviceProviderInfo.daysSinceStarted}
        createdOn={serviceProviderInfo.createdOn}
        primaryTrade={serviceProviderInfo.primaryTrade}
        contactOptions={contactOptions}
        onContactClick={handleContactClick}
        onBeginAlignmentClick={handleBeginAlignmentClick}
        doNotUse={serviceProviderInfo.doNotUse}
        canEdit={canEditServiceProvider}
        isSPLogin={isServiceProvider}
        onDoNotUseClick={() => setIsDoNotUseModalOpen(true)}
        onChangePasswordClick={() => setIsChangePasswordModalOpen(true)}
      />
      {!isServiceProvider && (
        <>
          <DoNotUsePanel serviceProviderId={id} />
          <DoNotUseReasonModal
            serviceProviderId={id}
            doNotUse={serviceProviderInfo.doNotUse}
            isOpen={isDoNotUseModalOpen}
            onClose={() => setIsDoNotUseModalOpen(false)}
          />
          <ServiceProviderChangePasswordModal
            serviceProviderId={id}
            providerName={serviceProviderInfo.name}
            isOpen={isChangePasswordModalOpen}
            onClose={() => setIsChangePasswordModalOpen(false)}
          />
        </>
      )}
      <FormProvider {...methods}>
        <div className={styles.tabsContainer}>
          <Tabs
            value={uiActiveTab}
            onChange={(value) =>
              navigate({
                to: "/service-providers/$id",
                params: { id },
                search: { tab: (value || "owner-info") as TabConfig["value"] },
              })
            }
            className={styles.tabs}
          >
            <ScrollArea
              orientation={"horizontal"}
              style={{ background: "var(--bg-neutral-default)" }}
            >
              <Tabs.List className={styles.tabsList}>
                {detailsTabs.map((tab) => {
                  if (
                    tab.value === "business-info" &&
                    serviceProviderInfo?.type === "Independent Tech"
                  )
                    return null;
                  return (
                    <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
                      {tab.label}
                    </Tabs.Tab>
                  );
                })}
              </Tabs.List>
            </ScrollArea>

            {/* TOP-LEVEL EDIT/SAVE/CANCEL REMOVED — section-level edit inside JsonFormBuilder */}
            {detailsTabs.map((tab) => {
              if (tab.value === "business-info" && serviceProviderInfo?.type === "Independent Tech")
                return null;
              return (
                <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
                  {isCurrentTabLoading ? (
                    <TabContentFormSkeleton />
                  ) : uiActiveTab === tab.value && tab.value === "vehicle" ? (
                    <VehicleAndUniforms ref={vehicleAndUniformsRef} />
                  ) : uiActiveTab === tab.value && tab.component ? (
                    <>{tab.component(id, serviceProviderInfo?.type)}</>
                  ) : (
                    <>
                      {uiActiveTab === tab.value && tab.panel?.(id)}
                      {currentTabFormData && !tab.component && (
                        <JsonFormBuilder
                          jsonFromSchema={currentTabFormData}
                          viewMode={true}
                          autoExpandWithData={true}
                          isEdit={true}
                          enableSectionEdit={true}
                          onSectionSave={handleSectionSave}
                          isSectionSaving={isSaving}
                          canEdit={canEditServiceProvider}
                        />
                      )}
                    </>
                  )}
                </Tabs.Panel>
              );
            })}
          </Tabs>
        </div>
      </FormProvider>
    </Flex>
  );
}
